import { IPayPalPaymentResponse } from "../../Pages/Root/IPayPalPaymentResponse";
import { OnApproveActions, OnApproveData, OnClickActions, OnShippingAddressChangeActions, OnShippingAddressChangeData, OnShippingOptionsChangeActions, OnShippingOptionsChangeData, PayPalNamespace, loadScript } from "@paypal/paypal-js";

export enum PayPalCaller {
    BasketPage = 1,
    PaymentPage = 2,
    SignInPage = 3,
}

export interface IBeginPayPalResponse {
    payPalOrderId: string;
    redirectUrl: string;
}

interface IPayPalAddress {
    city: string;
    countryCode: string;
    postalCode: string;
    state: string;
}

// Adds errors that are on the live PayPal types, but not in the package definitions.
interface OnShippingAddressChangeDataX extends OnShippingAddressChangeData {
    errors?: {
        COUNTRY_ERROR?: string;
        METHOD_UNAVAILABLE?: string;
    };
}

// Adds errors and a reject method that accepts those errors. Fixes the duff PayPal NPM definitions.
interface OnShippingAddressChangeActionsX extends OnShippingAddressChangeActions {
    errors?: {
        COUNTRY_ERROR?: string;
        METHOD_UNAVAILABLE?: string;
    };
    reject(reason?: unknown): Promise<void>;
}

interface OnShippingOptionsChangeDataX extends OnShippingOptionsChangeData {
    errors?: {
        COUNTRY_ERROR?: string;
        METHOD_UNAVAILABLE?: string;
    };
}

interface OnShippingOptionsChangeActionsX extends OnShippingOptionsChangeActions {
    errors?: {
        COUNTRY_ERROR?: string;
        METHOD_UNAVAILABLE?: string;
    };
    reject(reason?: unknown): Promise<void>;
}

export class PayPal {
    // BEGIN: PayPal bug tracking.
    // These fields prefixed with bugtrack are used to try and track down in what situations a shipping address might be null.
    private bugtrackBadShippingAddressCount = 0;
    private bugtrackShippingAddressAppliedCount = 0;
    private bugtrackReloadSkips = 0;
    private bugtrackReloads = 0;
    // END: PayPal bug tracking.

    private idempotencyKey: string;
    private lastCurrency: string = "";
    private localeUrlPrefix = document.body.dataset.localeUrlPrefix;
    private redirectionUrl: string;
    private shippingAddress: IPayPalAddress;
    private payPalClientId: string;
    private selectedShippingOption: string;
    private selectedStore: string;

    public static Init(pageValidationCallback: (data: any, action: OnClickActions) => Promise<any>, payPalCaller: PayPalCaller): PayPal {
        return new PayPal(pageValidationCallback, payPalCaller);
    }

    private payPalButtons: any;

    constructor(private pageValidationCallback: (data: any, action: OnClickActions) => Promise<any>, private payPalCaller: PayPalCaller) {
        let idempotencyKeyElement = document.getElementById("IdempotencyKey") as HTMLInputElement;

        if (!idempotencyKeyElement) {
            // PayPal not available, basket may be empty.
            return;
        }

        this.idempotencyKey = idempotencyKeyElement.value;

        let currencyIsoCode = (document.getElementById("BasketTotals_CurrencyIsoCode") as HTMLInputElement).value;
        this.payPalClientId = (document.getElementById("PayPalClientId") as HTMLInputElement).value;

        this.reloadPayPal(currencyIsoCode);
    }

    public parseDeliverySelection(): string {
        let selectedDeliveryRadio = <HTMLInputElement>document.querySelector('input[name = "DeliveryServices.SelectedDeliveryType"]:checked');

        if (selectedDeliveryRadio) {
            return selectedDeliveryRadio.value;
        }

        // No delivery services selected, we could be on the sign-in or payment pages which just have the PayPal button.
        selectedDeliveryRadio = <HTMLInputElement>document.querySelector('input[name = "DeliveryServices.SelectedDeliveryType"]');

        if (selectedDeliveryRadio) {
            return selectedDeliveryRadio.value;
        }

        return "0"; // Equivalent of NotSet enum, allows us to post to the server.
    }

    private parseSelectedStore(): string {
        let element = <HTMLInputElement>document.getElementById("DeliveryServices_DeliveryStoreId");

        if (element) {
            return element.value;
        }

        return "";
    }

    public reloadPayPal(currency: string): void {
        if (currency === this.lastCurrency) {
            // Reloading the same currency causes multiple PayPal buttons to pile up on the screen.
            this.bugtrackReloadSkips++;
            return;
        }

        this.bugtrackReloads++;
        this.lastCurrency = currency;

        loadScript({
            clientId: this.payPalClientId,
            components: "buttons,messages",
            currency: currency,
            disableFunding: ["card"],
            enableFunding: "paylater",
            intent: "authorize",
        })
            .then((paypal) => {
                this.createPayPalButton(paypal);
            });
    }

    private createPayPalButton(paypal: PayPalNamespace): void {

        // Show the '--or--' divider that separates the payment buttons.
        document.querySelectorAll(".or-hr").forEach(element => {
            element.classList.remove("hidden");
        });

        let ppContainer = document.getElementById("paypal-button-container");
        // PayPal buttons can be hidden when the basket is empty, the site / locale does not accept PayPal or a single use voucher is in the basket.
        if (ppContainer) {
            if (typeof paypal !== 'undefined') { // PayPal can sometimes block or fail to load due to network errors.
                this.payPalButtons = paypal
                    .Buttons({
                        createOrder: () => this.onCreateOrder(),
                        onApprove: (data, actions) => this.onApprove(data, actions),
                        onClick: (data, actions) => this.pageValidationCallback(data, actions),
                        onError: (err) => this.onError(err),
                        onShippingAddressChange: (data, actions) => this.onShippingAddressChange(data, actions),
                        onShippingOptionsChange: (data, actions) => this.onShippingOptionsChange(data, actions),
                        style: {
                            height: 44,
                            disableMaxWidth: true
                        },
                    });
                this.payPalButtons.render('#paypal-button-container');
            }
        }
    }

    private giftCardData(): any {
        let postData = {
            giftCardNumber: "",
            giftCardVerificationCode: "",
            payPalCaller: this.payPalCaller,
        };

        let paymentTypeGiftCardSelected = <HTMLInputElement>document.getElementById("PaymentTypeGiftCardSelected");
        if (paymentTypeGiftCardSelected && paymentTypeGiftCardSelected.checked === true) {
            let giftCardNumberElement = (<HTMLInputElement>document.getElementById("GiftCardNumber"));
            let giftCardVerificationCodeElement = (<HTMLInputElement>document.getElementById("GiftCardVerificationCode"));

            postData.giftCardNumber = giftCardNumberElement.value;
            postData.giftCardVerificationCode = giftCardVerificationCodeElement.value;
        }

        return postData;
    }

    private async onCreateOrder(): Promise<any> {
        try {
            let postData = this.giftCardData();

            this.selectedShippingOption = this.parseDeliverySelection();
            this.selectedStore = this.parseSelectedStore();
            postData.deliveryType = this.selectedShippingOption;
            postData.selectedStore = this.selectedStore;

            const response = await fetch(this.localeUrlPrefix + "/ws/begin-paypal-order", {
                method: "post",
                headers: { "content-type": "application/json" },
                body: JSON.stringify(postData)
            });

            if (!response.ok) {
                let err = new Error(`Unable to call begin-paypal-order due to comms error, status ${response.status}.`) as any;
                err.code = response.status;
                return Promise.reject(err);
            }

            const data: IBeginPayPalResponse = await response.json();

            // If there is a redirect then server side processing took over (error or paypal not needed).
            if (data.redirectUrl) {
                // Have to set the redirect URL here as onError will fire after returning a null payPalOrderId.
                this.redirectionUrl = data.redirectUrl;
                let err = new Error(`Redirecting to '${data.redirectUrl}' due to server side check.`) as any;
                err.code = 301;
                return Promise.reject(err);
            }

            return data.payPalOrderId;
        } catch (e) {
            this.trackPayPalError("onCreateOrder", e);
        }
    }

    private async onApprove(data: OnApproveData, actions: OnApproveActions): Promise<void> {
        try {
            // The call to PayPal can take a few seconds so show a busy modal.
            let modalDivElement = <HTMLDivElement>document.getElementById("paypal-processing-modal");
            if (modalDivElement) {
                window.Modal.OpenModal(modalDivElement, () => { });
            }

            // BEGIN: PayPal bug tracking.
            if (!this.shippingAddress) {
                this.trackPayPalError("onApprove-check", "");

                // BUGTRACK: We've not been supplied a shipping address which we need to continue.
                // The only thing we can do is restart the PayPal UI as we only have a restart action available to us.
                // The above code will have logged the issue and this exit here will prevent a server side error.
                // Once the source of this null / empty shipping address is found, we can remove this code.
                // It may be that PayPal does not send is the address via onShippingAddressChange as they don't have one.
                actions.restart();
                return;
            }
            // END: PayPal bug tracking.

            let postData = this.giftCardData();
            postData.idempotencyKey = this.idempotencyKey;
            postData.payPalOrderId = data.orderID;
            postData.selectedShippingOption = this.selectedShippingOption;
            postData.shippingAddress = this.shippingAddress;
            postData.selectedStore = this.selectedStore;

            const response = await fetch(this.localeUrlPrefix + "/ws/store-paypal-auth", {
                method: "post",
                headers: { "content-type": "application/json" },
                body: JSON.stringify(postData)
            });

            const details = await response.json();
            this.onApproveDetailsReceived(details, actions);
        } catch (rejectReason) {
            this.trackPayPalError("onApprove", rejectReason);
        }
    }

    private async onShippingAddressChange(data: OnShippingAddressChangeDataX, actions: OnShippingAddressChangeActionsX): Promise<any> {
        try {
            if (!data?.shippingAddress?.countryCode) {
                this.bugtrackBadShippingAddressCount++;
                // Country data not passed in, PayPal has failed this account on their website and passed us an address we can't use.

                actions.reject(data.errors.COUNTRY_ERROR);
                return;
            }

            let postData = this.giftCardData();
            // Save the shipping details as we don't get them again until after we authorise PayPal but need them before for totals and delivery.
            this.shippingAddress = data.shippingAddress;
            postData.payPalOrderId = data.orderID;
            postData.selectedShippingOption = this.selectedShippingOption;
            postData.shippingAddress = this.shippingAddress;
            postData.selectedStore = this.selectedStore;

            this.bugtrackShippingAddressAppliedCount++;

            const res = await fetch(this.localeUrlPrefix + "/ws/update-paypal-shipping", {
                method: "post",
                headers: { "content-type": "application/json" },
                body: JSON.stringify(postData)
            });

            if (!res?.ok) {
                // Http error, tell PayPal that we don't support this shipping destination.
                return actions.reject(data.errors.COUNTRY_ERROR);
            }

            return res.json();
        } catch (e) {
            this.trackPayPalError("onShippingAddressChange", e);
            // PayPal does not have a generic error so use the country one.
            return actions.reject(data.errors.COUNTRY_ERROR);
        }
    }

    private async onShippingOptionsChange(data: OnShippingOptionsChangeDataX, actions: OnShippingOptionsChangeActionsX): Promise<any> {
        try {
            let postData = this.giftCardData();

            // Save the shipping details as we don't get them again until after we authorise PayPal but need them before for totals and delivery.
            this.selectedShippingOption = data.selectedShippingOption.id;
            postData.payPalOrderId = data.orderID;
            postData.selectedShippingOption = this.selectedShippingOption;
            postData.shippingAddress = this.shippingAddress;

            const res = await fetch(this.localeUrlPrefix + "/ws/update-paypal-shipping", {
                method: "post",
                headers: { "content-type": "application/json" },
                body: JSON.stringify(postData)
            });

            if (!res?.ok) {
                // Http error, tell PayPal that we don't support this shipping destination / method.
                return actions.reject(data.errors.METHOD_UNAVAILABLE);
            }

            return await res.json();
        } catch (e) {
            this.trackPayPalError("onShippingOptionsChange", e);
            return actions.reject(data.errors.METHOD_UNAVAILABLE);
        }
    }

    private trackPayPalError(context: string, exception: any): void {
        const message = "PayPal client failure: " +
            "context: " + context +
            "bugtrackBadShippingAddressCount: " + this.bugtrackBadShippingAddressCount +
            "bugtrackShippingAddressAppliedCount: " + this.bugtrackShippingAddressAppliedCount +
            "bugtrackReloadSkips: " + this.bugtrackReloadSkips +
            "bugtrackReloads: " + this.bugtrackReloads;

        window.IssueLogger.log(message, context, exception);
    }

    private onApproveDetailsReceived(details: IPayPalPaymentResponse, actions: OnApproveActions): void {
        if (details.redirectUrl) {
            window.location.href = details.redirectUrl;
            return;
        }
        if (details.paymentError) {
            window.Modal.CloseModal();
            return actions.restart();
        }
    }

    private onError(err: any): void {
        if (this.redirectionUrl) {
            window.location.href = this.redirectionUrl;
            return;
        }

        if (err.code !== 301 && err.code !== 429) {
            window.IssueLogger.log("PayPal API call failed", "PayPal JS API", err);
        }
    }
}
